/**
 * MobileFleetInvoicesPage — the fleet customer's "Invoices" tab.
 *
 * A flat list of issued invoices, each showing its total and whether it is paid,
 * bounded by a date window. The window defaults to the last seven days (inclusive of
 * today); a segmented control switches to All time, and a popup takes a custom range.
 *
 * WHY A DEFAULT WINDOW AT ALL
 * ---------------------------
 * A fleet customer with a hundred vehicles accumulates invoices continuously. Opening
 * on everything means a long list, a slow first paint on mobile data, and the thing
 * they actually came for — the invoice from this week's service — somewhere below the
 * fold. Seven days is the answer to "did the bill for Tuesday come through".
 *
 * WHAT IS NOT HERE
 * ----------------
 * No payment. The portal has never taken money and this screen does not change that;
 * the paid/unpaid badge reports a state the workshop sets. Drafts and voided invoices
 * are excluded by the server, so nothing here can show a bill that was never issued.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, ChevronDown, Download, FileText, Loader2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    fetchFleetInvoices,
    downloadFleetInvoicePdf,
    selectFleetInvoices,
    selectFleetInvoicesLoading,
    selectFleetInvoicesError,
    selectFleetInvoiceDownloadingUuid,
} from '../../store/slices/fleetInvoiceSlice';
import { lastSevenDays, rangeLabel, shortDay, today } from '../../utils/dayRange';
// Customer-facing: a missing amount is an em dash, not $0.00 — see utils/money.js.
import { formatAud as money } from '../../utils/money';
import MobileListScreen from './MobileListScreen';
import useGroupedList from './useGroupedList';
import BottomSheet from './BottomSheet';
import { ListEmpty, ListError, ListLoading } from './ListStates';
import PullToRefresh from './PullToRefresh';

const PAGE_SIZE = 50;

// ── Date range popup ─────────────────────────────────────────────────────────

/** Custom range picker, in the shared bottom sheet. */
const RangeSheet = ({ open, initial, onApply, onClose }) => {
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [error, setError] = useState(null);

    // Re-seed each time it opens so a cancelled edit does not persist into the next.
    useEffect(() => {
        if (open) {
            setFrom(initial.from);
            setTo(initial.to);
            setError(null);
        }
    }, [open, initial.from, initial.to]);

    if (!open) return null;

    const apply = () => {
        // Checked here as well as on the server. The server's 400 is the real guard,
        // but a customer who dragged the dates the wrong way round should be told by
        // the control they are touching, not by a failed request.
        if (from && to && from > to) {
            setError('The start date must be before the end date.');
            return;
        }
        onApply({ from: from || undefined, to: to || undefined });
    };

    return (
        <BottomSheet
            open={open}
            title="Custom range"
            onClose={onClose}
            footer={
                <button type="button" onClick={apply}
                        className="w-full rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white">
                    Apply
                </button>
            }
        >
            <div className="space-y-3">
                <div>
                    <label htmlFor="range-from" className="mb-1 block text-xs font-medium text-gray-600">
                        From
                    </label>
                    {/* Native date inputs on purpose: iOS and Android render their own
                        wheel pickers, which are far better than anything hand-built
                        and already handle locale and accessibility. */}
                    <input
                        id="range-from"
                        type="date"
                        value={from || ''}
                        max={today()}
                        onChange={(e) => setFrom(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
                    />
                </div>

                <div>
                    <label htmlFor="range-to" className="mb-1 block text-xs font-medium text-gray-600">
                        To
                    </label>
                    <input
                        id="range-to"
                        type="date"
                        value={to || ''}
                        max={today()}
                        onChange={(e) => setTo(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
                    />
                </div>

                {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
        </BottomSheet>
    );
};

// ── Filter bar ───────────────────────────────────────────────────────────────

const MODES = [
    { key: 'week',   label: '7 days' },
    { key: 'all',    label: 'All'    },
    { key: 'custom', label: <><Calendar size={12} className="mr-1 inline" />Custom</> },
];

const PAYMENT_KEYS = ['unpaid', 'paid'];

const PAYMENT_MODES = [
    { key: 'unpaid', label: 'Not paid' },
    { key: 'paid',   label: 'Paid'     },
];



// ── Invoice row ──────────────────────────────────────────────────────────────

const InvoiceRow = ({ invoice, onDownload, downloading }) => (
    <li className="border-b border-gray-100 bg-white px-4 py-3">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="truncate font-mono text-sm font-semibold text-gray-900">
                    {invoice.invoiceNumber}
                </p>
                <p className="mt-0.5 truncate text-xs text-gray-500">
                    {shortDay(invoice.issueDate)}
                    {invoice.vehicleRego && ` · ${invoice.vehicleRego}`}
                </p>
            </div>

            <div className="shrink-0 text-right">
                <p className="text-sm font-bold text-gray-900">{money(invoice.totalAmount)}</p>
                {/* `paid` is computed server-side from the status enum. Re-deriving it
                    here from the status string would be a second, weaker copy of a rule
                    the front end does not own — see FleetPortalInvoiceService.isSettled. */}
                <span
                    className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        invoice.paid
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-amber-50 text-amber-700'
                    }`}
                >
                    {invoice.paid ? 'Paid' : 'Unpaid'}
                </span>
            </div>
        </div>

        {/* Balance shown only when part-paid: on a fully unpaid invoice it repeats the
            total, and on a paid one it is zero. Both are noise. */}
        {!invoice.paid
            && invoice.balanceDue !== null
            && invoice.balanceDue !== undefined
            && Number(invoice.balanceDue) !== Number(invoice.totalAmount) && (
            <p className="mt-1 text-xs text-amber-700">
                {money(invoice.balanceDue)} still owing
            </p>
        )}

        {invoice.hasPdf && (
            <button
                type="button"
                onClick={() => onDownload(invoice)}
                disabled={downloading}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 disabled:opacity-50"
            >
                {downloading
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Download size={12} />}
                PDF
            </button>
        )}
    </li>
);

// ── Page ─────────────────────────────────────────────────────────────────────

const MobileFleetInvoicesPage = () => {
    const dispatch = useAppDispatch();

    const invoices = useAppSelector(selectFleetInvoices);
    const loading = useAppSelector(selectFleetInvoicesLoading);
    const error = useAppSelector(selectFleetInvoicesError);
    const downloadingUuid = useAppSelector(selectFleetInvoiceDownloadingUuid);

    const [mode, setMode] = useState('week');
    const [range, setRange] = useState(() => lastSevenDays());
    const [sheetOpen, setSheetOpen] = useState(false);
    const [payment, setPayment] = useState('unpaid');

    /*
     * Split on the server's `paid` flag, never on the status string — `paid` is computed
     * server-side and re-deriving it here would be a second, weaker copy of a rule this
     * app does not own. The same fact drives the badge on each row.
     *
     * Grouping, sorting and counting live in useGroupedList, shared with the three other
     * filtered phone lists. Note the split applies to the page the server returned for
     * the selected period — "Paid" means paid within that window, and the period control
     * above is what widens it.
     */
    const { groups, counts } = useGroupedList(invoices, {
        keys: PAYMENT_KEYS,
        groupBy: (inv) => (inv.paid ? 'paid' : 'unpaid'),
        sortBy: (inv) => inv.issueDate,
    });

    const shown = groups[payment];

    const load = useCallback((next) => {
        dispatch(fetchFleetInvoices({
            page: 0,
            size: PAGE_SIZE,
            from: next.from,
            to: next.to,
        }));
    }, [dispatch]);

    // The default window is computed once at mount rather than on every render: a
    // rolling "last 7 days" recomputed mid-session would change the query underneath
    // the list the customer is reading.
    useEffect(() => {
        load(range);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pickMode = (next) => {
        if (next === 'custom') {
            setSheetOpen(true);
            return;
        }
        // All time is expressed as BOTH bounds absent, which the API omits from the
        // query string entirely — an empty `from=` is a parse failure server-side,
        // not an absent filter.
        const nextRange = next === 'week' ? lastSevenDays() : { from: undefined, to: undefined };
        setMode(next);
        setRange(nextRange);
        load(nextRange);
    };

    const applyCustom = (nextRange) => {
        setMode('custom');
        setRange(nextRange);
        setSheetOpen(false);
        load(nextRange);
    };

    const download = (invoice) => {
        dispatch(downloadFleetInvoicePdf({
            uuid: invoice.uuid,
            invoiceNumber: invoice.invoiceNumber,
        }));
    };

    return (
        <MobileListScreen
            title="Invoices"
            filters={[
                {
                    ariaLabel: 'Invoice period',
                    options: MODES,
                    value: mode,
                    onChange: pickMode,
                    // The active window in words, under the control that set it — a
                    // segmented button reading "Custom" does not say which dates apply.
                    detail: (
                        <p className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500">
                            <Calendar size={11} />
                            {rangeLabel(range)}
                        </p>
                    ),
                },
                {
                    ariaLabel: 'Payment status',
                    options: PAYMENT_MODES.map((m) => ({ ...m, badge: counts[m.key] })),
                    value: payment,
                    onChange: setPayment,
                },
            ]}
            loading={loading}
            error={error}
            onRefresh={() => load(range)}
            items={shown}
            renderItem={(inv) => (
                <InvoiceRow
                    key={inv.uuid}
                    invoice={inv}
                    onDownload={download}
                    downloading={downloadingUuid === inv.uuid}
                />
            )}
            empty={{
                icon: FileText,
                // "No invoices" alone would read as "you have never been billed" while a
                // 7-day window is applied. The window is named, and so is the payment
                // filter — an empty Paid tab with unpaid invoices sitting behind the
                // other pill is otherwise baffling.
                title: payment === 'paid'
                    ? 'Nothing paid in this period'
                    : 'Nothing outstanding in this period',
                detail: rangeLabel(range),
                action: mode !== 'all' && (
                    <button type="button" onClick={() => pickMode('all')}
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600">
                        Show all invoices <ChevronDown size={12} />
                    </button>
                ),
            }}
        >
            <RangeSheet
                open={sheetOpen}
                initial={{ from: range.from || '', to: range.to || today() }}
                onApply={applyCustom}
                onClose={() => setSheetOpen(false)}
            />
        </MobileListScreen>
    );
};

export default MobileFleetInvoicesPage;
